import React from 'react';
import cn from 'classnames';
import styles from './Box.less';

type CommonBoxProps = {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

type directionVertical = 'column' | 'column-reverse';
type directionHorizontal = 'row' | 'row-reverse';

type alignVertical = 'top' | 'center' | 'bottom' | 'stretch';
type alignHorizontal = 'left' | 'center' | 'right' | 'stretch';

type justifyVertical = 'top' | 'center' | 'bottom' | 'space-between' | 'space-around';
type justifyHorizontal = 'left' | 'center' | 'right' | 'space-between' | 'space-around';

type ColumnBoxProps = {
  /**
   * Sets the direction in which childrens will be placed
   */
  direction: directionVertical;

  /**
   * Sets the way how childrens will be aligned
   */
  align: alignHorizontal;

  /**
   * Sets the way how childrens will be justified
   */
  justify: justifyVertical;

  /**
   * Specifies whether childrens are forced into a single line or can be wrapped onto multiple lines
   */
  wrap?: boolean;
};

type RowBoxProps = {
  /**
   * Sets the direction in which childrens will be placed
   */
  direction: directionHorizontal;

  /**
   * Sets the way how childrens will be aligned
   */
  align: alignVertical;

  /**
   * Sets the way how childrens will be justified
   */
  justify: justifyHorizontal;

  /**
   * Specifies whether childrens are forced into a single line or can be wrapped onto multiple lines
   */
  wrap?: boolean;
};

type BoxProps = CommonBoxProps & Partial<ColumnBoxProps | RowBoxProps>;

const getAlign = (align: alignVertical | alignHorizontal) => {
  switch (align) {
    case 'left':
    case 'top':
      return 'flex-start';

    case 'right':
    case 'bottom':
      return 'flex-end';

    default:
      return align;
  }
};

const getJustify = (justify: justifyVertical | justifyHorizontal) => {
  switch (justify) {
    case 'left':
    case 'top':
      return 'flex-start';

    case 'right':
    case 'bottom':
      return 'flex-end';

    default:
      return justify;
  }
};

/**
 * A thin wrapper around css flex-boxes. Intead of passing a style object or a css class name you
 * you can pass direction, align and justify props directly
 */
const Box: React.SFC<BoxProps> = ({
  direction,
  align,
  justify,
  wrap,
  children,
  className,
  style,
}): JSX.Element => (
  <div
    className={cn(styles.box, className)}
    style={{
      alignItems: getAlign(align),
      flexFlow: `${direction} ${wrap ? 'wrap' : 'nowrap'}`,
      justifyContent: getJustify(justify),
      ...style,
    }}
  >
    {children}
  </div>
);

Box.defaultProps = {
  align: 'stretch',
  direction: 'row',
  justify: 'left',
  wrap: false,
};

export default Box;
